import { ReactPortal, useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import ShapeEditorOverlay from './ShapeEditorOverlay';
import { LatLng, LeafletMouseEventHandlerFn } from 'leaflet';
import { createPortal } from 'react-dom';
import { GeofenceShapeModel } from '@casops/store/apis/geofencesApi';
import { OVERLAY_PORTAL_ID } from '../../mapConfig';

interface UseShapeEditorOverlayProps {
  initialRadius?: GeofenceShapeModel['radius'];
}

interface UseShapeEditorOverlayReturn {
  setCircleRadius: (radius: number) => void;
  overlay: ReactPortal | null;
}

export const useShapeEditorOverlay = ({
  initialRadius,
}: UseShapeEditorOverlayProps): UseShapeEditorOverlayReturn => {
  const map = useMap();

  const [circleRadius, setCircleRadius] = useState<number | undefined>(undefined);
  const [mousePosition, setMousePosition] = useState<LatLng | undefined>(undefined);

  // If there is an existing radius, populate the radius state.
  useEffect(() => {
    if (typeof initialRadius === 'number') {
      setCircleRadius(Math.round(initialRadius));
    }
  }, [initialRadius]);

  // Track the cursor positon.
  useEffect(() => {
    const onMouseMove: LeafletMouseEventHandlerFn = e => setMousePosition(e.latlng.wrap());
    map.on('mousemove', onMouseMove);

    return () => {
      map.off('mousemove', onMouseMove);
    };
  }, [map]);

  // Get the DOM node where the overlay will be rendered via a portal.
  const externalPortalElement = document.getElementById(OVERLAY_PORTAL_ID);

  /*
    Create a portal to render the HTML overlay outside of the map. This is to bypass React Leaflet node management limitations.
    Attempting to render the custom HTML within React Leaflet can lead to runtime errors if the markup is conditionally rendered.
  */
  const overlay =
    externalPortalElement !== null
      ? createPortal(
          <ShapeEditorOverlay position={mousePosition} circleRadius={circleRadius} />,
          externalPortalElement,
        )
      : null;

  return {
    setCircleRadius,
    overlay,
  };
};

export default useShapeEditorOverlay;
